import { AdminLook, isAdminLook, Look } from "./looks";

/** Last known platform look, so the first paint uses it (no flash). */
export const LOOK_STORAGE_KEY = "cb-admin-look";

export function readCachedLook(): AdminLook {
  try {
    const v = window.localStorage.getItem(LOOK_STORAGE_KEY);
    return isAdminLook(v) ? v : "classic";
  } catch {
    return "classic";
  }
}

export function writeCachedLook(look: AdminLook): void {
  try {
    window.localStorage.setItem(LOOK_STORAGE_KEY, look);
  } catch {
    /* storage blocked (private mode): the look still applies for this visit */
  }
}

/**
 * The look to use after the GET: an older backend (null) = classic and no
 * setting screen; a failed GET keeps the cached look; a value is cached.
 */
export async function resolveLook(
  fetch: () => Promise<{ look: AdminLook } | null>,
): Promise<{ look: AdminLook; settingAvailable: boolean }> {
  let res: { look: AdminLook } | null;
  try {
    res = await fetch();
  } catch {
    return { look: readCachedLook(), settingAvailable: true };
  }
  const look = res && isAdminLook(res.look) ? res.look : "classic";
  writeCachedLook(look);
  return { look, settingAvailable: res !== null };
}

const FONT_LINK_ID = "cb-look-font";

/** Loads only the active look's font (one `<link>`) and points `--cb-font` (index.css) at it. */
export function ensureLookFont(look: Look, doc: Document = document): void {
  doc.documentElement.style.setProperty("--cb-font", look.font);
  const existing = doc.getElementById(FONT_LINK_ID) as HTMLLinkElement | null;
  if (!look.fontHref) {
    existing?.remove();
    return;
  }
  if (existing?.getAttribute("href") === look.fontHref) return;
  const link = existing ?? doc.createElement("link");
  link.id = FONT_LINK_ID;
  link.rel = "stylesheet";
  link.href = look.fontHref;
  if (!existing) doc.head.appendChild(link);
}
